import useGetEmailSatisfaction from '@/api/hcp/voiceofthecustomer/useGetEmailSatisfaction';
import { AdobeAnalyticsConst } from '@/hooks/AdobeAnalyticsHooks/adobeAnalyticsConstants/adobeAnalyticsConstants';
import { AdditionalChannelTabs, Channel } from '@/models';
import {
  useAllChannelTabData,
  useEmailData,
  useEventData,
  useFaceToFaceData,
  useWebsiteData,
} from '@/pages/ChannelFocus/useChannelCoverageHooks';
import NavigationTabList, {
  type NavigationTab,
  type NavigationTabListProps,
} from '@/storybook/components/NavigationTabList/NavigationTabList';
import Score from '@/storybook/components/Score/Score';
import { AppRoutes } from '@/utils';
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChannelFocusNavigation.module.css';

interface ChannelFocusNavigationProps {
  activeChannel: Channel | AdditionalChannelTabs.ALL_CHANNEL | undefined;
  children: ReactNode;
}

const NAVIGATION_TAB_ADOBE_ANALYTICS_CONFIG: NavigationTabListProps['adobeTrackingAnalytics'] = {
  trackingPosition: AdobeAnalyticsConst.POSITIONS.CHANNEL_FOCUS_TABS,
  trackingPrefixName: AdobeAnalyticsConst.LINKCLICK_NAMES.DASHBOARD_NAVIGATION_TABS,
};

interface ChannelTabScoreProps {
  testIdPrefix: string;
  label: string;
  currentValue: number | undefined;
  prevValue: number | undefined;
  isActive: boolean;
}

const ChannelTabScore = ({ testIdPrefix, label, currentValue, prevValue, isActive }: ChannelTabScoreProps) => {
  const hasValue = currentValue !== undefined;
  const arrowValue = hasValue && prevValue ? (currentValue / prevValue - 1) * 100 : undefined;

  return (
    <Score
      value={hasValue ? currentValue * 100 : 0}
      isRoundedValue={true}
      displayZero={hasValue}
      valueSuffix="%"
      arrowValue={arrowValue}
      size="SM"
      label={label}
      labelColor={isActive ? 'BLUE' : 'GREY'}
      align="center"
      data-testid={testIdPrefix}
      value-testid={testIdPrefix + '-value'}
      dash-value-testid={testIdPrefix + '-dash-value'}
      arrow-testid={testIdPrefix + '-arrow-value'}
      reverseTitles={true}
    />
  );
};

interface ChannelTabSatisfaction {
  label: string;
  current: number | undefined;
  previous: number | undefined;
}

const createChannelTab = (
  id: AdditionalChannelTabs | Channel,
  label: string,
  link: AppRoutes,
  isLoading: boolean,
  coverageLabel: string,
  currentValue: number | undefined,
  prevValue: number | undefined,
  isActive: boolean,
  satisfaction?: ChannelTabSatisfaction,
): NavigationTab => {
  const coverageScore = (
    <ChannelTabScore
      testIdPrefix={id + '-tab-score'}
      label={coverageLabel}
      currentValue={currentValue}
      prevValue={prevValue}
      isActive={isActive}
    />
  );

  return {
    'id': id,
    'data-testid': id,
    'label': label,
    'link': link,
    'isLoading': isLoading,
    'additionalContent': satisfaction ? (
      <div className={styles.tabScores}>
        {coverageScore}
        <ChannelTabScore
          testIdPrefix={id + '-tab-satisfaction-score'}
          label={satisfaction.label}
          currentValue={satisfaction.current}
          prevValue={satisfaction.previous}
          isActive={isActive}
        />
      </div>
    ) : (
      coverageScore
    ),
  };
};

const useChannelNavigationTabs = (
  activeChannel: Channel | AdditionalChannelTabs.ALL_CHANNEL | undefined,
): NavigationTab[] => {
  const {
    isLoading: isLoadingAllChannel,
    current: currentAllChannel,
    previous: previousAllChannel,
  } = useAllChannelTabData();
  const {
    isLoading: isLoadingFaceToFace,
    current: currentFaceToFace,
    previous: previousFaceToFace,
  } = useFaceToFaceData();
  const { isLoading: isLoadingEmail, current: currentEmail, previous: previousEmail } = useEmailData();
  const {
    isLoading: isLoadingEmailSatisfaction,
    current: currentEmailSatisfaction,
    previous: previousEmailSatisfaction,
  } = useGetEmailSatisfaction();
  const { isLoading: isLoadingEvent, current: currentEvent, previous: previousEvent } = useEventData();
  const { isLoading: isLoadingWebsite, current: currentWebsite, previous: previousWebsite } = useWebsiteData();

  const { t } = useTranslation();
  const coverageLabel = t('main.channelFocus.navigationTabs.coverage');
  const satisfactionLabel = t('main.channelFocus.navigationTabs.satisfaction');

  const getIsChannelActive = useCallback(
    (tabId: AdditionalChannelTabs | Channel) => activeChannel === tabId,
    [activeChannel],
  );

  return useMemo(
    () => [
      createChannelTab(
        AdditionalChannelTabs.ALL_CHANNEL,
        'ALL CHANNELS',
        AppRoutes.CHANNEL_ALL_CHANNELS,
        isLoadingAllChannel,
        coverageLabel,
        currentAllChannel,
        previousAllChannel,
        getIsChannelActive(AdditionalChannelTabs.ALL_CHANNEL),
      ),
      createChannelTab(
        Channel.VISITS_1_TO_1,
        '1 TO 1 VISIT',
        AppRoutes.CHANNEL_FOCUS_1_TO_1,
        isLoadingFaceToFace,
        coverageLabel,
        currentFaceToFace,
        previousFaceToFace,
        getIsChannelActive(Channel.VISITS_1_TO_1),
      ),
      createChannelTab(
        Channel.EMAIL,
        'EMAIL',
        AppRoutes.CHANNEL_FOCUS_EMAIL,
        isLoadingEmail || isLoadingEmailSatisfaction,
        coverageLabel,
        currentEmail,
        previousEmail,
        getIsChannelActive(Channel.EMAIL),
        {
          label: satisfactionLabel,
          current: currentEmailSatisfaction,
          previous: previousEmailSatisfaction,
        },
      ),
      createChannelTab(
        Channel.EVENT,
        'EVENT',
        AppRoutes.CHANNEL_FOCUS_EVENT,
        isLoadingEvent,
        coverageLabel,
        currentEvent,
        previousEvent,
        getIsChannelActive(Channel.EVENT),
      ),
      createChannelTab(
        Channel.WEBSITE,
        'WEBSITE',
        AppRoutes.CHANNEL_FOCUS_WEBSITE,
        isLoadingWebsite,
        coverageLabel,
        currentWebsite,
        previousWebsite,
        getIsChannelActive(Channel.WEBSITE),
      ),
    ],
    [
      coverageLabel,
      currentAllChannel,
      currentEmail,
      currentEmailSatisfaction,
      currentEvent,
      currentFaceToFace,
      currentWebsite,
      getIsChannelActive,
      isLoadingAllChannel,
      isLoadingEmail,
      isLoadingEmailSatisfaction,
      isLoadingEvent,
      isLoadingFaceToFace,
      isLoadingWebsite,
      previousAllChannel,
      previousEmail,
      previousEmailSatisfaction,
      previousEvent,
      previousFaceToFace,
      previousWebsite,
      satisfactionLabel,
    ],
  );
};

const ChannelFocusNavigation = ({ activeChannel, children }: ChannelFocusNavigationProps) => {
  const navigationTabList = useChannelNavigationTabs(activeChannel);

  return (
    <>
      <NavigationTabList tabs={navigationTabList} adobeTrackingAnalytics={NAVIGATION_TAB_ADOBE_ANALYTICS_CONFIG} />
      {children}
    </>
  );
};

export default ChannelFocusNavigation;
